import { useMemo } from 'react';
import useDrawStore from '@/stores/drawStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Palette,
  PaintBucket,
  Minus,
  Type,
  RotateCw,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Layers,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const COLORS = [
  '#000000', '#1e1e1e', '#374151', '#6b7280',
  '#ef4444', '#f97316', '#eab308', '#22c55e',
  '#14b8a6', '#06b6d4', '#3b82f6', '#6366f1',
  '#8b5cf6', '#a855f7', '#ec4899', '#f43f5e',
  '#ffffff', 'transparent',
];

const STROKE_WIDTHS = [1, 2, 3, 4, 6, 8, 12, 16];

const FONT_SIZES = [12, 14, 16, 18, 20, 24, 28, 32, 40, 48, 64];

const FONT_FAMILIES = [
  { value: 'Inter, sans-serif', label: 'Inter' },
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: 'monospace', label: 'Monospace' },
  { value: 'Comic Sans MS, cursive', label: 'Comic Sans' },
];

export default function PropertiesPanel() {
  const {
    elements,
    selectedIds,
    activeStyles,
    applyStylesToSelected,
    updateElement,
    unlockElement,
    showLayers,
    toggleLayers,
    showIconLibrary,
    toggleIconLibrary,
  } = useDrawStore();

  // Get selected elements
  const selectedElements = useMemo(() => {
    return elements.filter(el => selectedIds.includes(el.id));
  }, [elements, selectedIds]);

  // Get common styles from selection
  const commonStyles = useMemo(() => {
    if (selectedElements.length === 0) return activeStyles;
    
    const first = selectedElements[0];
    return {
      strokeColor: selectedElements.every(el => el.strokeColor === first.strokeColor) 
        ? first.strokeColor : null,
      fillColor: selectedElements.every(el => el.fillColor === first.fillColor) 
        ? first.fillColor : null,
      strokeWidth: selectedElements.every(el => el.strokeWidth === first.strokeWidth) 
        ? first.strokeWidth : null,
      fontSize: selectedElements.every(el => el.fontSize === first.fontSize) 
        ? first.fontSize : null,
      fontFamily: selectedElements.every(el => el.fontFamily === first.fontFamily) 
        ? first.fontFamily : null,
      opacity: selectedElements.every(el => el.opacity === first.opacity) 
        ? first.opacity : null,
    };
  }, [selectedElements, activeStyles]);

  const hasSelection = selectedIds.length > 0;
  const singleSelection = selectedElements.length === 1;
  const selectedElement = singleSelection ? selectedElements[0] : null;

  return (
    <div className="flex flex-col h-full bg-background border-l border-border w-[280px]">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-border">
        <h3 className="font-semibold">Propriétés</h3>
        {hasSelection && (
          <span className="text-xs text-muted-foreground">
            {selectedIds.length} élément{selectedIds.length > 1 ? 's' : ''}
          </span>
        )}
      </div>

      <ScrollArea className="flex-1">
        <div className="p-3 space-y-6">
          {/* Quick Actions */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">
              Actions rapides
            </Label>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                size="sm"
                className="justify-start"
                onClick={toggleIconLibrary}
              >
                <Palette className="h-4 w-4 mr-2" />
                {showIconLibrary ? 'Masquer' : 'Icônes'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="justify-start"
                onClick={toggleLayers}
              >
                <Layers className="h-4 w-4 mr-2" />
                {showLayers ? 'Masquer' : 'Calques'}
              </Button>
            </div>
          </div>

          <Separator />

          {/* Colors */}
          <div className="space-y-3">
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">
              Couleurs
            </Label>
            
            {/* Stroke Color */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Minus className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Contour</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {COLORS.map((color) => (
                  <button
                    key={color}
                    className={cn(
                      "w-6 h-6 rounded border transition-all",
                      commonStyles.strokeColor === color && "ring-2 ring-primary ring-offset-2",
                      color === 'transparent' && "bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI4IiBoZWlnaHQ9IjgiPjxyZWN0IHdpZHRoPSI0IiBoZWlnaHQ9IjQiIGZpbGw9IiNjY2MiLz48cmVjdCB4PSI0IiB5PSI0IiB3aWR0aD0iNCIgaGVpZ2h0PSI0IiBmaWxsPSIjY2NjIi8+PC9zdmc+')]"
                    )}
                    style={{ backgroundColor: color === 'transparent' ? undefined : color }}
                    onClick={() => applyStylesToSelected({ strokeColor: color })}
                    data-testid={`stroke-color-${color}`}
                  />
                ))}
              </div>
            </div>

            {/* Fill Color */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <PaintBucket className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Remplissage</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {COLORS.map((color) => (
                  <button
                    key={color}
                    className={cn(
                      "w-6 h-6 rounded border transition-all",
                      commonStyles.fillColor === color && "ring-2 ring-primary ring-offset-2",
                      color === 'transparent' && "bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI4IiBoZWlnaHQ9IjgiPjxyZWN0IHdpZHRoPSI0IiBoZWlnaHQ9IjQiIGZpbGw9IiNjY2MiLz48cmVjdCB4PSI0IiB5PSI0IiB3aWR0aD0iNCIgaGVpZ2h0PSI0IiBmaWxsPSIjY2NjIi8+PC9zdmc+')]"
                    )}
                    style={{ backgroundColor: color === 'transparent' ? undefined : color }}
                    onClick={() => applyStylesToSelected({ fillColor: color })}
                    data-testid={`fill-color-${color}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <Separator />

          {/* Stroke Width */}
          <div className="space-y-3">
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">
              Épaisseur du trait
            </Label>
            <div className="flex gap-1">
              {STROKE_WIDTHS.map((width) => (
                <Button
                  key={width}
                  variant={commonStyles.strokeWidth === width ? 'default' : 'outline'}
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => applyStylesToSelected({ strokeWidth: width })}
                >
                  {width}
                </Button>
              ))}
            </div>
          </div>

          <Separator />

          {/* Typography */}
          <div className="space-y-3">
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">
              Typographie
            </Label>
            
            {/* Font Family */}
            <div className="space-y-2">
              <Label className="text-xs">Police</Label>
              <Select
                value={commonStyles.fontFamily || 'Inter, sans-serif'}
                onValueChange={(value) => applyStylesToSelected({ fontFamily: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FONT_FAMILIES.map((font) => (
                    <SelectItem key={font.value} value={font.value}>
                      <span style={{ fontFamily: font.value }}>{font.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Font Size */}
            <div className="space-y-2">
              <Label className="text-xs">Taille</Label>
              <Select
                value={String(commonStyles.fontSize || 16)}
                onValueChange={(value) => applyStylesToSelected({ fontSize: parseInt(value) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FONT_SIZES.map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size}px
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator />

          {/* Opacity */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                Opacité
              </Label>
              <span className="text-sm text-muted-foreground">
                {Math.round((commonStyles.opacity || 1) * 100)}%
              </span>
            </div>
            <Slider
              value={[(commonStyles.opacity || 1) * 100]}
              min={0}
              max={100}
              step={5}
              onValueChange={([value]) => applyStylesToSelected({ opacity: value / 100 })}
            />
          </div>

          {/* Element-specific properties */}
          {singleSelection && selectedElement && (
            <>
              <Separator />
              
              <div className="space-y-3">
                <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                  Élément sélectionné
                </Label>
                
                {/* Position */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">X</Label>
                    <Input
                      type="number"
                      value={Math.round(selectedElement.x)}
                      onChange={(e) => updateElement(selectedElement.id, { x: parseFloat(e.target.value) })}
                      className="h-8"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Y</Label>
                    <Input
                      type="number"
                      value={Math.round(selectedElement.y)}
                      onChange={(e) => updateElement(selectedElement.id, { y: parseFloat(e.target.value) })}
                      className="h-8"
                    />
                  </div>
                </div>

                {/* Size */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Largeur</Label>
                    <Input
                      type="number"
                      value={Math.round(selectedElement.width)}
                      onChange={(e) => updateElement(selectedElement.id, { width: parseFloat(e.target.value) })}
                      className="h-8"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Hauteur</Label>
                    <Input
                      type="number"
                      value={Math.round(selectedElement.height)}
                      onChange={(e) => updateElement(selectedElement.id, { height: parseFloat(e.target.value) })}
                      className="h-8"
                    />
                  </div>
                </div>

                {/* Rotation */}
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <RotateCw className="h-4 w-4 text-muted-foreground" />
                    <Label className="text-xs">Rotation</Label>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      value={Math.round(selectedElement.rotation || 0)}
                      onChange={(e) => updateElement(selectedElement.id, { rotation: parseFloat(e.target.value) })}
                      className="h-8"
                    />
                    <span className="self-center text-muted-foreground">°</span>
                  </div>
                </div>

                {/* Visibility & Lock */}
                <div className="flex gap-2">
                  <Button
                    variant={selectedElement.visible ? 'outline' : 'secondary'}
                    size="sm"
                    className="flex-1"
                    onClick={() => updateElement(selectedElement.id, { visible: !selectedElement.visible })}
                  >
                    {selectedElement.visible ? (
                      <>
                        <Eye className="h-4 w-4 mr-2" />
                        Visible
                      </>
                    ) : (
                      <>
                        <EyeOff className="h-4 w-4 mr-2" />
                        Masqué
                      </>
                    )}
                  </Button>
                  <Button
                    variant={selectedElement.locked ? 'secondary' : 'outline'}
                    size="sm"
                    className="flex-1"
                    onClick={() => {
                      if (selectedElement.locked) {
                        unlockElement(selectedElement.id);
                      } else {
                        updateElement(selectedElement.id, { locked: true });
                      }
                    }}
                  >
                    {selectedElement.locked ? (
                      <>
                        <Lock className="h-4 w-4 mr-2" />
                        Verrouillé
                      </>
                    ) : (
                      <>
                        <Unlock className="h-4 w-4 mr-2" />
                        Libre
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
